<template>
  <v-navigation-drawer
    v-model="drawer"
    app
    dark
    class="drawer"
    expand-on-hover
    fixed
    :mini-variant.sync="mini"
    permanent
  >
    <v-list>
      <v-list-item
        v-for="(link, index) of quicklinks"
        :key="'quicklink-' + index"
        :title="link.title"
        :to="{name: link.route}"
      >
        <v-list-item-icon>
          <v-icon>{{ link.icon }}</v-icon>
        </v-list-item-icon>

        <v-list-item-content>
          <v-list-item-title>{{ link.title }}</v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </v-list>

    <v-divider />

    <v-list subheader>
      <v-subheader
        :class="{'d-none' : mini}"
      >
        Tools
      </v-subheader>
      <v-list-item
        v-for="(link, index) of tools"
        :key="'toolslink-' + index"
        :title="link.title"
        :to="{ name: link.route }"
      >
        <v-list-item-action v-if="link.has_count">
          <v-badge
            :content="link.count"
            :value="link.count"
            color="red"
            overlap
          >
            <v-icon>{{ link.icon }}</v-icon>
          </v-badge>
        </v-list-item-action>
        <v-list-item-icon v-else>
          <v-icon>{{ link.icon }}</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>
            {{ link.title }}
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </v-list>

    <template #append>
      <v-list-item
        class="create-record"
        link
      >
        <v-list-item-action
          v-if="canCreateRecord"
          large
          color="primary"
          title="Create Record"
          @click="_ => onOpenCreateRecord()"
        >
          <v-icon>add</v-icon>
        </v-list-item-action>
        <v-list-item-content>
          <v-list-item-title
            v-if="canCreateRecord"
            @click="_ => onOpenCreateRecord()"
          >
            New Record
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <v-divider />

      <v-list-item
        v-if="!isGlobalScope"
        two-line
        link
        @click="showTeamDialog = true"
      >
        <v-list-item-action>
          <v-icon>group</v-icon>
        </v-list-item-action>
        <v-tooltip
          top
        >
          <template #activator="{ on: teamTooltipOn, attrs: teamTooltipAttrs }">
            <v-list-item-content
              v-bind="teamTooltipAttrs"
              v-on="teamTooltipOn"
            >
              <v-list-item-title>{{ currentTeamName }}</v-list-item-title>
              <v-list-item-subtitle>Change current team</v-list-item-subtitle>
            </v-list-item-content>
          </template>
          <span>Click to Change Active Team</span>
        </v-tooltip>
        <v-list-item-action>
          <v-tooltip
            right
          >
            <template #activator="{ on: teamBtnTooltipOn, attrs: teamBtnTooltipAttrs }">
              <v-btn
                icon
                v-bind="teamBtnTooltipAttrs"
                v-on="teamBtnTooltipOn"
                @click.stop="_ => onGoToTeamPage()"
              >
                <v-icon color="grey lighten-1">
                  arrow_forward
                </v-icon>
              </v-btn>
            </template>
            <span>Go to Team's Page</span>
          </v-tooltip>
        </v-list-item-action>
      </v-list-item>

      <v-tooltip top>
        <template #activator="{ on: scopeTooltipOn, attrs: scopeTooltipAttrs }">
          <v-list-item
            v-if="!changingScope"
            two-line
            link
            v-bind="scopeTooltipAttrs"
            :class="{'primary' : isGlobalScope}"
            v-on="scopeTooltipOn"
            @click="_ => toggleUserScope()"
          >
            <v-list-item-action>
              <v-icon>change_circle</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>Current Scope</v-list-item-title>
              <v-list-item-subtitle>{{ currentUserScope }}</v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action v-if="isGlobalScope">
              <v-tooltip
                right
              >
                <template #activator="{ on: scopeBtnTooltipOn, attrs: scopeBtnTooltipAttrs }">
                  <v-btn
                    icon
                    v-bind="scopeBtnTooltipAttrs"
                    v-on="scopeBtnTooltipOn"
                    @click.stop="_ => goToGlobalView()"
                  >
                    <v-icon color="white">
                      arrow_forward
                    </v-icon>
                  </v-btn>
                </template>
                <span>Go to Global View</span>
              </v-tooltip>
            </v-list-item-action>
          </v-list-item>
        </template>
        <span>Toggle Current Scope</span>
      </v-tooltip>

      <v-list-item
        v-if="changingScope"
        two-line
      >
        <v-list-item-action>
          <v-progress-circular
            indeterminate
            color="primary"
            size="24"
          />
        </v-list-item-action>
        <v-list-item-content>
          <v-list-item-title>Changing Scope...</v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <ChangeCurrentTeamDialog
        v-model="showTeamDialog"
      />
    </template>
  </v-navigation-drawer>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'

import actions from '@/data/Permissions/enums.json'
import { alertHandler } from '@/alertHandler'
import { parseErrorResponseMessage } from '@/utilities'

import { INTEGRATION_NAMESPACE } from '@/store/integrations'
import { SHARED_NAMESPACE } from '@/store/shared'
import { USER_NAMESPACE } from '@/store/user'

import ChangeCurrentTeamDialog from '@/components/Dialogs/Team/ChangeCurrentTeamDialog.vue'
import { PERMISSIONS_NAMESPACE } from '@/store/permissions'

export default {
  name: 'NavDrawer',

  components: {
    ChangeCurrentTeamDialog,
  },

  props: {
    drawerOpen: {
      type: Boolean,
      required: true,
    },
  },

  setup () {
    return {
      ...alertHandler(),
    }
  },

  data () {
    return {
      changingScope: false,
      defaultLinks: [
        { default: true, icon: 'home', route: 'dashboard', title: 'Dashboard' },
        { default: true, icon: 'folder_open', route: 'data-records', title: 'Records' },
      ],

      drawer: true,
      mini: true,
      requestInterval: null,
      resourceRequestItem: {
        action: actions.Platform.Tools.PartySurveys,
        count: 0,
        has_count: true,
        icon: 'assignment_turned_in',
        route: 'resourceRequests',
        title: 'Resource Requests',
      },

      showTeamDialog: false,
      toolLinks: [],
    }
  },

  computed: {
    canCreateRecord () {
      return this.can(actions.Record.Create)
    },

    currentTeamName () {
      return this.currentUserActiveTeam?.name || 'No Active Team'
    },

    currentUserScope () {
      return this.currentUser?.scope_is_global ? 'Global' : 'Active Team'
    },

    drawerState: {
      get () {
        return this.drawerOpen
      },

      set (newValue) {
        if (newValue !== this.drawerOpen) this.$emit('update-drawer', newValue)
      },
    },

    isGlobalScope () {
      return this.currentUser?.scope_is_global
    },

    quicklinks () {
      return this.defaultLinks.filter(link => this.can(link.action) || link.default)
    },

    tools () {
      return this.toolLinks.filter(link => this.can(link.action) || link.default)
    },

    ...mapGetters([
      'currentUser',
      'currentUserActiveTeam',
    ]),

    ...mapGetters(INTEGRATION_NAMESPACE, [
      'ecoPortalEnabled',
    ]),

    ...mapGetters(SHARED_NAMESPACE, [
      'features',
    ]),

    ...mapGetters(USER_NAMESPACE, [
      'can',
    ]),

  },

  async created () {
    await this.getFeatures()

    this.mergePartyLink()
    this.calendarLinks()
    this.advancedSearchLink()
    this.EOIRCaseInfoLinks()

    this.loadAdminLinks()
    this.checkEcoPortalEnabled().
      finally(_ => this.loadEcoPortal())

    this.loadTeamTools()
  },

  beforeDestroy () {
    if (this.requestInterval) {
      clearInterval(this.requestInterval)
      this.requestInterval = null
    }
  },

  methods: {
    EOIRCaseInfoLinks () {
      if (!this.features?.eoir?.enabled) return

      const link = {
        action: actions.Platform.EOIRCaseInfo.ViewAllCases,
        icon: 'mdi-monitor-eye',
        route: 'eoir-case-info',
        title: 'EOIR Case Info',
      }

      this.defaultLinks.push(link)
    },

    advancedSearchLink () {
      const link = {
        action: actions.Platform.Tools.AdvancedSearch,
        icon: 'search',
        route: 'advanced-search',
        title: 'Advanced Search',
      }

      this.defaultLinks.push(link)
    },

    calendarLinks () {
      const links = [
        {
          action: actions.Platform.Calendar.ViewCalendar,
          icon: 'mdi-calendar-account',
          route: 'calendar',
          title: 'Calendar',
        },
        {
          action: actions.Platform.Calendar.ViewCalendar,
          icon: 'mdi-calendar-heart',
          route: 'day-at-a-glance',
          title: 'Day at a Glance',
        },
      ]

      this.defaultLinks.push(...links)
    },

    goToGlobalView () {
      this.$router.push({ name: 'userGlobalView' })
    },

    loadAdminLinks () {
      this.toolLinks.push({
        action: actions.Platform.Tools.ImportParties,
        icon: 'group_add',
        route: 'import-party',
        title: 'Import Party',
      })

      this.toolLinks.push({
        action: actions.Platform.Tools.Reports,
        icon: 'mdi-finance',
        route: 'management-reports',
        title: 'Reports',
      })

      this.toolLinks.push({
        action: actions.Platform.Tools.DataRecovery,
        icon: 'settings_backup_restore',
        route: 'data-recovery',
        title: 'Data Recovery',
      })

      this.toolLinks.push({
        action: actions.Platform.Tools.PartySurveys,
        icon: 'quiz',
        route: 'party-surveys',
        title: 'Party Surveys',
      })

      this.toolLinks.push(this.resourceRequestItem)

      this.requestInterval = setInterval(_ => {
        this.getResourceAccessCount().
          then(response => {
            if (response && response.count !== undefined) {
              this.resourceRequestItem.count = response.count
            }
          })
      }, 10000) // Update every 10 seconds
    },

    loadEcoPortal () {
      if (this.ecoPortalEnabled) {
        this.toolLinks.push({
          action: actions.Platform.Tools.PartySurveys,
          icon: 'table_rows ',
          route: 'integrationEcoPortal',
          title: 'ECO Portal Exports',
        })
      }
    },

    loadTeamTools () {
      this.toolLinks.push({
        action: actions.Platform.Management.Team.Access,
        icon: 'manage_accounts',
        route: 'userTeams',
        title: 'My  Teams',
      })
    },

    mergePartyLink () {
      // Checking against the lower permission of the same group MergeParties vs CopyParties
      this.defaultLinks.push({
        action: actions.Platform.Tools.MergeParties,
        icon: 'people_alt',
        route: 'merge-party',
        title: 'Merge Party',
      })
    },

    onGoToTeamPage () {
      if (this.currentUserActiveTeam) {
        this.$router.push({ name: 'userTeamsDetails', params: { id: this.currentUserActiveTeam.id } })
      } else {
        this.displayAlert('info', 'No active team to navigate to. Please select an active team first.')
      }
    },

    onOpenCreateRecord () {
      this.setCreateRecordDialog(true)
    },

    onSelectTeam () {
      this.currentTeam = this.selectedTeam
      this.showTeamDialog = false
    },

    toggleUserScope () {
      this.changingScope = true
      this.changeUserScope().
        then(_ => {
          this.displayAlert('success', 'User scope changed successfully.')
          this.$router.go(0)
        }).
        catch(e => this.displayAlert('error', parseErrorResponseMessage(e))).
        finally(_ => (this.changingScope = false))
    },

    ...mapMutations([
      'setCreateRecordDialog',
    ]),

    ...mapActions(INTEGRATION_NAMESPACE, [
      'checkEcoPortalEnabled',
    ]),

    ...mapActions(SHARED_NAMESPACE, [
      'getFeatures',
    ]),

    ...mapActions(USER_NAMESPACE, [
      'changeUserScope',
    ]),

    ...mapActions(PERMISSIONS_NAMESPACE, [
      'getResourceAccessCount',
    ]),

  },
}
</script>
